"use client";
import Link from "next/link";
import { Button } from "@/components/ui";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Logo } from "@/components/xui";
import { Marquee } from "@padi/xui/marquee";
import { ROUTES, PUBLIC_ENV } from "@padi/lib/constants";
import { cn } from "@/lib/utils";
import type { Profile } from "@padi/lib/hooks/use-auth";

const Header = ({ profile }: { profile?: Profile | null }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const user = profile;
  const { seller, buyer } = PUBLIC_ENV;

  const links = [
    { name: "Farmer/Aggregator", href: `${seller}${ROUTES.SIGNIN}` },
    { name: "Buyer", href: `${buyer}${ROUTES.SIGNIN}` },
    { name: "FAQ", href: "/faq" },
  ];

  const navLinks = [
    { name: "About", href: "/#about" },
    { name: "How it Works", href: "/#howitworks" },
    { name: "Contact", href: "/#contact" },
  ];

  function Commodities({ className }: { className?: string }) {
    type Commodity = {
      label: string;
      price: string;
      unit: string;
    };

    const commodities: Commodity[] = [
      { label: "Raw Cashew Nut (RCN)", price: "$1,800", unit: "metric ton" },
      { label: "Sesame Seeds", price: "$1,250", unit: "metric ton" },
      { label: "Hibiscus Flower", price: "$1,900", unit: "metric ton" },
      { label: "Soybeans", price: "$520", unit: "metric ton" },
      { label: "Dry Ginger", price: "$9,500", unit: "metric ton" },
    ];
    return (
      <Marquee
        pauseOnHover
        className={cn("[--duration:15s] [--gap:4px]", className)}
      >
        {commodities.map((item, index) => (
          <span key={item.label} className="flex items-center">
            <span>
              {item.label}:{" "}
              <span className="font-bold">
                {item.price}/{item.unit}
              </span>
            </span>
            {<span className="mx-2 ">|</span>}
          </span>
        ))}
      </Marquee>
    );
  }
  return (
    <header>
      {/* Top Bar - Hidden on mobile */}
      <div className="hidden bg-primary text-white py-4 pr-8 md:flex pl-32 gap-4 space-between w-full items-center">
        <Commodities />
        <div className="flex ml-auto space-x-4">
          {links.map((link) => (
            <Link href={link.href} className="hover:underline" key={link.name}>
              {link.name}
            </Link>
          ))}
        </div>
      </div>
      {/* </div> */}

      {/* Mobile Top Bar - Simplified */}
      <div className="md:hidden bg-primary text-white py-2 px-4">
        <Commodities className="text-xs" />
      </div>

      {/* Main Header */}
      <div className="bg-[#f2f2f2] py-4 px-4 md:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-6 text-foreground font-medium">
            {navLinks.map((link) => (
              <Link
                href={link.href}
                className="hover:text-primary"
                key={link.name}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link href="/crops">
              <Button variant="peach">Browse Crop</Button>
            </Link>
            <Link href={user ? "/home" : "/signin"}>
              <Button>{user ? "Dashboard" : "Login/Signup"}</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-muted-dark">
            <nav className="flex flex-col space-y-4 mt-4">
              {navLinks.map((link) => (
                <Link
                  href={link.href}
                  className="hover:text-primary font-medium"
                  key={link.name}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Action Buttons */}
            <div className="flex flex-col space-y-3 mt-6">
              <Link href="/crops">
                <Button variant="peach" size="sm" fullWidth>
                  Browse Crop
                </Button>
              </Link>
              <Link href={user ? "/home" : "/signin"}>
                <Button size="sm" fullWidth>
                  {user ? "Dashboard" : "Login/Signup"}
                </Button>
              </Link>
            </div>

            {/* Mobile Links */}
            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-muted-dark">
              {links.map((link) => (
                <Link
                  href={link.href}
                  className="text-sm text-muted-foreground hover:text-primary"
                  key={link.name}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
